/**
 * ImageThumbnailWithActions - Reusable image thumbnail with hover overlay actions.
 *
 * Provides a consistent UX pattern for image management across the application:
 * - Dark semi-transparent overlay appears on hover or keyboard focus
 * - TrashIcon for remove (white bg, turns red on hover/focus)
 * - Optional PencilIcon for edit (white bg, turns blue on hover/focus)
 * - Built-in ConfirmDialog for removal confirmation
 * - Keyboard accessible: overlay shows on focus-within, buttons have focus styles
 */
import { useState } from 'react';
import { PhotoIcon } from '@heroicons/react/24/outline';
import { TrashIcon, PencilIcon } from '@/components/ui/Icons';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';

interface ImageThumbnailWithActionsProps {
  /** URL of the image to display. Ignored when `children` is provided. */
  imageUrl?: string;
  /** Alt text for the image */
  alt?: string;
  /** Size of the thumbnail */
  size: { width: number; height: number };
  /** Callback when image is confirmed for removal */
  onRemove?: () => void;
  /** Callback when edit is clicked (no confirmation needed) */
  onEdit?: () => void;
  /** Tooltip for the remove button */
  removeLabel?: string;
  /** Tooltip for the edit button */
  editLabel?: string;
  /** Title for the confirmation dialog */
  confirmTitle?: string;
  /** Message for the confirmation dialog */
  confirmMessage?: string;
  /** Whether a removal operation is in progress */
  isRemoving?: boolean;
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Optional label to show at the bottom of the thumbnail */
  label?: string;
  /** Additional CSS classes for the container */
  className?: string;
  /** Optional custom image content rendered in place of the `imageUrl <img>`.
   *  Useful for CSS-cropped previews (`<CroppedThumbnail>`) where the image
   *  isn't a single URL. Sized to fill the container. */
  children?: React.ReactNode;
}

export function ImageThumbnailWithActions({
  imageUrl,
  alt = 'Image',
  size,
  onRemove,
  onEdit,
  removeLabel = 'Remove image',
  editLabel = 'Edit',
  confirmTitle = 'Remove Image',
  confirmMessage = 'Are you sure you want to remove this image?',
  isRemoving = false,
  disabled = false,
  label,
  className = '',
  children,
}: ImageThumbnailWithActionsProps) {
  const [showConfirm, setShowConfirm] = useState(false);

  const handleRemoveClick = () => {
    if (disabled) return;
    setShowConfirm(true);
  };

  const handleConfirmRemove = () => {
    onRemove?.();
    setShowConfirm(false);
  };

  const handleEditClick = () => {
    if (disabled) return;
    onEdit?.();
  };

  const hasActions = onRemove || onEdit;

  return (
    <>
      <div
        className={`relative group ${className}`}
        style={{ width: size.width, height: size.height }}
      >
        {/* Image container. `children` slot wins over `imageUrl` so callers
            can drop in a custom rendering (e.g. CSS-cropped preview for an
            unsaved Targeted). Falls back to URL → placeholder otherwise. */}
        <div className="w-full h-full bg-gray-100 rounded border border-gray-200 overflow-hidden">
          {children ? (
            children
          ) : imageUrl ? (
            <img
              src={imageUrl}
              alt={alt}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <PhotoIcon className="w-6 h-6 text-gray-400" />
            </div>
          )}
        </div>

        {/* Hover/focus overlay with actions */}
        {hasActions && !disabled && (
          <div
            className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 pointer-events-none group-hover:pointer-events-auto group-focus-within:pointer-events-auto transition-opacity flex items-center justify-center gap-1.5 rounded"
            aria-hidden={false}
          >
            {onEdit && (
              <button
                type="button"
                onClick={handleEditClick}
                className="p-1.5 bg-white text-gray-700 rounded hover:bg-blue-500 hover:text-white focus:bg-blue-500 focus:text-white focus:outline-none focus:ring-2 focus:ring-blue-400 transition-colors"
                title={editLabel}
                aria-label={editLabel}
              >
                <PencilIcon className="w-3.5 h-3.5" />
              </button>
            )}
            {onRemove && (
              <button
                type="button"
                onClick={handleRemoveClick}
                className="p-1.5 bg-white text-gray-700 rounded hover:bg-red-500 hover:text-white focus:bg-red-500 focus:text-white focus:outline-none focus:ring-2 focus:ring-red-400 transition-colors"
                title={removeLabel}
                aria-label={removeLabel}
              >
                <TrashIcon className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}

        {/* Optional label at bottom */}
        {label && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-1 rounded-b">
            <p className="text-[10px] text-white truncate">{label}</p>
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      {onRemove && (
        <ConfirmDialog
          isOpen={showConfirm}
          onClose={() => setShowConfirm(false)}
          onConfirm={handleConfirmRemove}
          title={confirmTitle}
          message={confirmMessage}
          confirmText="Remove"
          cancelText="Cancel"
          variant="danger"
          isLoading={isRemoving}
        />
      )}
    </>
  );
}

export default ImageThumbnailWithActions;
